import { boolean, index, integer, jsonb, pgTable, text, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

// =============================================================================
// GAP-ADDRESS LOOP (docs/design/gap-address-loop-2026-07-05.md) — the owner's
// rulings, verbatim: click a scored gap -> a quick scoped conversation with
// Mira -> the answer lands in the permanent corpus WITH PROVENANCE so it
// "doesn't come up another time" -> automatic rescore -> surface the next
// unaddressed gap before shaping.
//
// TWO TABLES, additive-only (rule #11). Neither table is applied by this
// lane — docs/design/gap-address-neon.sql is the deliverable; the
// orchestrator (or a human) runs it against Neon. Mirrored here so
// packages/schema stays the single typed source of truth the moment it IS
// applied.
//
//   gap_address_sessions — the SCOPED CONVERSATION itself: one row per
//     "address this gap" click, holding the seed context (gap text, JD
//     requirement excerpt, the adjacent evidence the score narrative already
//     named), the turn transcript, and the OUTCOME — which achievement (for
//     an evidence gap) or which situation declaration (for a situation gap)
//     it produced, so a job's gap list can render "addressed - see [x]" and
//     never resurface a closed gap blind (the owner's frustration guard).
//     job_id is TEXT, matching the EXISTING, verified precedent every other
//     job-scoped table in this codebase already uses (jobScores.jobId,
//     tailoringRuns.jobId, resumes.jobId, coverLetters.jobId, alignment_
//     essays.job_id — see that file's own header for why) even though jobs.id
//     is itself a uuid primary key.
//
//   user_situation_declarations — the DATED DECLARATION shape for the
//     SITUATION gap class (visa/relocation/availability/etc). Per the
//     owner's ruling #4: "the real state of the situation can be asked of
//     the user and explained at that time because it's going to be user
//     specific and cannot be hard coded" — this table stores ONLY what the
//     user actually said, dated, tied to the gap that prompted it. It is
//     NOT a visa-rules engine (that stays parked) and it does not attempt to
//     interpret or validate the declaration — it is a fact capture, exactly
//     like achievements.sourceRef traces to the user's own words. A LATER
//     declaration for the same declarationKind supersedes the prior one for
//     DISPLAY purposes (current-state reads take the latest by created_at)
//     but the prior row is never deleted (nothing-unrecoverable) — the full
//     history of how the user's stated situation changed over time stays on
//     the record.
// =============================================================================

/** One turn in the scoped gap-address conversation transcript. Mirrors the
 *  shape InterviewTurnEngine's recentTranscript already uses (role/text) so
 *  a future shared transcript-renderer needs no translation layer. */
export interface GapAddressTurn {
  role: 'assistant' | 'user';
  text: string;
  /** ISO timestamp of this turn, for an honest ordered transcript render. */
  at: string;
}

/** Why the session is CLOSED (server sets this on commit or dismissal — the
 *  FE never invents a status). 'open' = conversation still in progress. */
export type GapAddressStatus = 'open' | 'evidence_committed' | 'situation_declared' | 'dismissed';

export const gapAddressSessions = pgTable(
  'gap_address_sessions',
  {
    id: id(),
    userId: uuid('user_id').notNull(),
    /** TEXT — matches jobs.id's existing FK convention across this codebase
     *  (see file header). */
    jobId: text('job_id').notNull(),
    /** Which search scenario's score this gap came from (job_scores.scenario_id) —
     *  nullable for a legacy pre-scenario score, mirroring job_scores itself. */
    scenarioId: text('scenario_id'),
    /** The stable per-score-row gap id (gapClassifier.ts's `${jobId}:${scenarioId}:${index}`)
     *  — lets the gap list resolve "is THIS exact gap addressed" without
     *  re-matching on the gap's own text (which can legitimately repeat
     *  across scenarios/rescores). */
    gapId: varchar('gap_id', { length: 300 }).notNull(),
    /** The gap's own text at the time the session opened (frozen copy — a
     *  later rescore may reword or drop the gap; this session's own record
     *  of what it was addressing stays stable regardless). */
    gapText: text('gap_text').notNull(),
    gapClass: varchar('gap_class', { length: 16 }).notNull(), // 'evidence' | 'situation'
    /** The JD requirement excerpt + the adjacent evidence the score narrative
     *  already named — the SEEDING context, frozen at session-open time so a
     *  later rescore can't retroactively change what this conversation was
     *  anchored to. */
    seedJdExcerpt: text('seed_jd_excerpt'),
    seedAdjacentEvidence: jsonb('seed_adjacent_evidence').$type<string[]>(),
    transcript: jsonb('transcript').$type<GapAddressTurn[]>().default([]).notNull(),
    status: varchar('status', { length: 24 }).default('open').notNull(),
    /** Set when status = 'evidence_committed' — the achievement this session
     *  produced, so "addressed - see [evidence]" can link straight to it. */
    resultAchievementId: uuid('result_achievement_id'),
    /** Set when status = 'situation_declared' — the declaration row this
     *  session produced. */
    resultDeclarationId: uuid('result_declaration_id'),
    /** How many turns have run — the "quick conversation, not a full
     *  interview" cap is enforced against this (GapAddressEngine.ts). */
    turnCount: integer('turn_count').default(0).notNull(),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('gap_address_sessions_user_idx').on(t.userId),
    jobIdx: index('gap_address_sessions_job_idx').on(t.jobId),
    gapIdx: index('gap_address_sessions_gap_idx').on(t.gapId),
  }),
);

export const userSituationDeclarations = pgTable(
  'user_situation_declarations',
  {
    id: id(),
    userId: uuid('user_id').notNull(),
    /** Which gap-address session produced this (provenance — traces the
     *  declaration back to the exact conversation + gap that prompted it). */
    sessionId: uuid('session_id').notNull(),
    jobId: text('job_id').notNull(),
    gapId: varchar('gap_id', { length: 300 }).notNull(),
    /** A short, stable key for WHAT aspect of the user's situation this is —
     *  free text, not an enum, because the visa/relocation/availability
     *  space is deliberately NOT hard-coded here (the owner's ruling: "user
     *  specific and cannot be hard coded"). Typical values: 'visa_status',
     *  'relocation', 'availability', 'work_authorisation' — but the column
     *  never constrains to a fixed list; a gap the classifier calls
     *  'situation' for a reason not yet named gets whatever key the
     *  conversation naturally surfaced. */
    declarationKind: varchar('declaration_kind', { length: 64 }).notNull(),
    /** The user's OWN words, verbatim (never a paraphrase, never a model
     *  inference about what it "really" means) — the no-invention law
     *  applied to a fact capture: this table states only what was said. */
    declarationText: text('declaration_text').notNull(),
    /** True while this is the CURRENT-STATE read for this (userId, jobId,
     *  declarationKind) tuple — a later declaration for the same kind sets
     *  the prior row's isCurrent to false (superseded, never deleted;
     *  nothing-unrecoverable). Reads that want "what's true today" filter on
     *  this; reads that want the full history ignore it. */
    isCurrent: boolean('is_current').default(true).notNull(),
    declaredAt: timestamp('declared_at', { withTimezone: true }).notNull(),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('user_situation_declarations_user_idx').on(t.userId),
    jobIdx: index('user_situation_declarations_job_idx').on(t.jobId),
    currentIdx: index('user_situation_declarations_current_idx').on(
      t.userId,
      t.declarationKind,
      t.isCurrent,
    ),
  }),
);
